import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import AcxLineGraph from "components/UI/Visualization/AcxLineGraph";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AcxExpansion from "components/UI/AcxExpansion";
import theme from "Theme/AppTheme";

type ChartData = (string | number)[];

type Props = {
    data: ChartData[];
    id: string;
    isExpanded: boolean;
    setIsExpanded: () => void;
    compareTotal?: boolean;
    toggleCompareTotal?: () => void;
    showCompareTotal?: boolean;
};

const ConversationsOverTime: React.FC<Props> = observer(
    ({
        id,
        data,
        isExpanded,
        setIsExpanded,
        compareTotal,
        toggleCompareTotal,
        showCompareTotal,
    }) => {
        const [renderedData, setRenderedData] = useState<ChartData[]>([[]]);

        const validData = (d) => {
            return (d?.flat().length ?? 0) > 1;
        };

        const isValidData = useMemo(
            () => validData(renderedData),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [renderedData],
        );

        useEffect(() => {
            if (compareTotal) {
                setRenderedData(data);
            } else {
                setRenderedData(
                    data.map((i) => {
                        // remove 2 elements starting at index 3 (the data series for Total Conversations)
                        const newData = [...i.slice(0, 3), ...i.slice(5)];
                        return newData;
                    }),
                );
            }
        }, [compareTotal, data]);

        return (
            <Grid paddingBottom={1} item width={"100%"}>
                <AcxExpansion
                    header={
                        <Grid fontWeight={600}>Conversations Over Time</Grid>
                    }
                    body={
                        <Grid
                            container
                            sx={{
                                borderBottomLeftRadius:
                                    theme.shape.borderRadius * 2,
                                borderBottomRightRadius:
                                    theme.shape.borderRadius * 2,
                                paddingRight: theme.spacing(2),
                                paddingLeft: theme.spacing(2),
                                paddingBottom: theme.spacing(1), // less padding on bottom to account for padding included in chart svg
                                backgroundColor: theme.palette.white.main,
                            }}
                        >
                            {showCompareTotal && (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    justifyContent={"flex-end"}
                                >
                                    <FormControlLabel
                                        id={"compare-to-previous-period-" + id}
                                        label={
                                            <Grid container alignItems="center">
                                                Compare to all conversations
                                            </Grid>
                                        }
                                        control={
                                            <Checkbox
                                                onChange={(e) => {
                                                    toggleCompareTotal?.();
                                                }}
                                                checked={compareTotal}
                                            />
                                        }
                                    />
                                </Grid>
                            )}
                            {isValidData && (
                                <AcxLineGraph
                                    showGrid
                                    chartAreaHeight={75}
                                    chartAreaWidth={"100%"}
                                    legendPosition={"none"}
                                    legendAlignment="center"
                                    data={renderedData}
                                    vAxisFormat="short"
                                    vAxisFontSize="12"
                                    hAxisFontSize="12"
                                    dataOpacity={1}
                                    chartHeight="150px"
                                    hideDataLabels
                                    hideDataMarkers
                                    usesHtmlTooltips
                                    colorScheme={[
                                        theme.palette.primary[700],
                                        theme.palette.gray[400],
                                    ]}
                                    chartId={id}
                                    disableSelection
                                />
                            )}
                            {!isValidData && (
                                <AcxEmptyState size="small" variant="empty" />
                            )}
                        </Grid>
                    }
                    conversationVariant
                    expanded={isExpanded}
                    onExpandChange={() => {
                        setIsExpanded();
                    }}
                    headerSx={{
                        height: "42px",
                        padding: theme.spacing(1),
                        paddingLeft: theme.spacing(2),
                        borderTopLeftRadius: theme.shape.borderRadius * 2,
                        borderTopRightRadius: theme.shape.borderRadius * 2,
                        borderBottomLeftRadius: isExpanded
                            ? "0"
                            : theme.shape.borderRadius * 2,
                        borderBottomRightRadius: isExpanded
                            ? "0"
                            : theme.shape.borderRadius * 2,
                        backgroundColor: theme.palette.white.main,
                    }}
                />
            </Grid>
        );
    },
);

export default ConversationsOverTime;
